import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import * as S from './styles';
import BatchDetailsHeader from './components/BatchDetailsHeader';
import DraftStatus from './components/DraftStatus';
import ApprovalStatus from './components/ApprovalStatus';
import JobDetailsTab from './components/JobDetailsTab';
import ProofsTab from './components/ProofsTab';
import StatusTab from './components/StatusTab';
import { useSubscription } from '@apollo/client';
import { FETCH_BATCHES } from '../../gql/FetchBatches';
import {
  abortBatch,
  approveBatch,
  batchSchedule,
  handleApproval,
  rescheduleBatch,
  getDraftBatchInfo,
  discardDraftBatch,
  rescheduleAdvancedBatch,
  hybridDiscardDraft,
  abortAdvancedBatch,
  rejectHybridBatch
} from '../../../../utils/services/statusMonitoringAPI';
import {
  JOB_ABORT_MESSAGES,
  JOB_APPROVAL_MESSAGES,
  JOB_RESCHEDULE_MESSAGES,
  JOB_DETAILS_UI,
  JOB_DISCARD_STATUS,
  JOB_REJECT_MESSAGES
} from './TranslationValues';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionContext } from '../../../../context/sessionContext';
import { getJobStatus, hybridDraftStatus } from '../../../../utils/constants/statusMonitoring';
import moment from 'moment';
import AbortionDetailsTab from './components/AbortionDetailsTab';

const NAMESPACES = [
  'JOB_ABORT_MESSAGES',
  'JOB_APPROVAL_MESSAGES',
  'JOB_RESCHEDULE_MESSAGES',
  'JOB_DETAILS_UI',
];

/**
 * BatchDetailsPanel refactored to use smaller components
 */
const BatchDetailsPanel = ({
  jobId,
  batchType,
  users,
  subscribed,
  filters,
  cardCount,
  setHybridInfo,
  setCurrentPage,
  setDraftBatchInfo,
  setActiveItem,
  setBatchType,
  batchData,
  showSidebar,
  isArchiveMode,
}) => {
  const { data, loading: dataLoading, error } = useSubscription(FETCH_BATCHES, {
    variables: {
      filter: {
        page: 1,
        startDate: filters?.filter?.startDate,
        endDate: filters?.filter?.endDate,
        jobId,
        batchType,
        limit: cardCount,
        ...(isArchiveMode ? { isArchived: true } : {}),
      },
    },
    skip: false,
    shouldResubscribe: true,
  });
  const batch = data?.batchDocumentStats?.data ? data.batchDocumentStats?.data[0] : undefined;

  const { t } = useTranslation(NAMESPACES);

  const [showReschedule, setShowReschedule] = useState(false);
  const [showApprove, setShowApprove] = useState(true);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [isApproving, setIsApproving] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showApprovalCard, setShowApprovalCard] = useState(false);
  const [isBatchTimePassed, setIsBatchTimePassed] = useState(false);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);
  const [canAbortBatch, setCanAbortBatch] = useState(false);

  useEffect(() => {
    if (batch?.scheduledTime) {
      const localMoment = moment.utc(batch.scheduledTime).local();
      setNewDate(localMoment.format('YYYY-MM-DD'));
      setNewTime(localMoment.format('HH:mm:ss'));
    }
    setShowApprove(canUserApprove());
  }, [batch?.id, batch?.scheduledTime]);

  useEffect(() => {
    setShowApprovalCard(false);
    const localMoment = moment.utc(batch?.scheduledTime).local();
    setIsBatchTimePassed(batch?.scheduledTime ? moment().isAfter(localMoment) : true);
    setCanAbortBatch(userDetails.id == batch?.triggeredBy && (batch?.status === 'SCHEDULED'));
  },[batch?.id]);

  useEffect(() => {
    if((batch?.status?.toLowerCase().startsWith('waiting_for_approval') || batch?.status === 'SCHEDULED') && (canUserApprove() || batch?.triggeredBy == userDetails.id)) {
      setShowApprovalCard(true);
    } else {
      setShowApprovalCard(false);
    }
    setShowApprove(canUserApprove());
    setCanAbortBatch(userDetails.id == batch?.triggeredBy && (batch?.status === 'SCHEDULED'));
  }, [batch?.status])

  useEffect(() => {
    if (batchType === 'HYBRID' && batch) {
      setHybridInfo({
        batchName: batch.batchName,
        splitOnCriteria: batch.splitType,
        pageCount: null,
        uploadedFile: null,
        hybridBatchId: jobId,
        batchInfo: batch,
      });
    }
  }, [batchType, batch]);

  const handleContinueEditing = async callback => {
    if (batchType === 'HYBRID' && batch) {
      setCurrentPage('hybridMail');
    } else {
      try {
        const response = await getDraftBatchInfo(batch?.id);
        if (response?.statusCode === 7039 && response?.data) {
          setDraftBatchInfo(response.data);
          setCurrentPage('createBatch');
        } else {
          message.error(
            t(JOB_DETAILS_UI.CONTINUE_DRAFT_ERROR.key, JOB_DETAILS_UI.CONTINUE_DRAFT_ERROR.value),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        callback();
      }
    }
  };

  const handleCancel = async callback => {
    if (batchType === 'HYBRID') {
      const payload = {
        batchId: batch?.id
      };
      try {
        const response = await hybridDiscardDraft(payload);
        if (response?.statusCode === 7021) {
          message.success(
            t(JOB_DISCARD_STATUS.DISCARD_SUCCESSFUL.key, JOB_DISCARD_STATUS.DISCARD_SUCCESSFUL.value),
          );
        }
        else if (response?.statusCode === 7022) {
          if (response?.errors[0]?.code === 7005) {
            message.error(
              t(JOB_DISCARD_STATUS.JOB_DOESNOT_EXIST.key, JOB_DISCARD_STATUS.JOB_DOESNOT_EXIST.value),
            );
          }
          else if (response?.errors[0]?.code === 7021) {
            message.error(
              t(JOB_DISCARD_STATUS.JOB_DISCARD_FAILED.key, JOB_DISCARD_STATUS.JOB_DISCARD_FAILED.value),
            );
          }
        }
      }
      catch (e) {
        message.error(
          t(JOB_DISCARD_STATUS.JOB_DISCARD_FAILURE_MSG.key, JOB_DISCARD_STATUS.JOB_DISCARD_FAILURE_MSG.value),
        );
      }
      finally {
        setTimeout(() => {
          callback();
        }, 1000);
      }
    }
    else if (batchType === 'ADVANCED') {
      try {
        const response = await discardDraftBatch(batch?.id);
        if (response?.statusCode === 7041) {
          message.success(t(JOB_DETAILS_UI.DRAFT_DISCARDED_MESSAGE.key, JOB_DETAILS_UI.DRAFT_DISCARDED_MESSAGE.value));
          const items = batchData?.batchDocumentStats?.data || [];
          const currentIndex = items.findIndex(item => item.id === batch?.id);
          let nextItemId = null;
          let nextBatchType = null;
          if (currentIndex !== -1) {
            if (currentIndex < items.length - 1) {
              nextItemId = items[currentIndex + 1]?.id;
              nextBatchType = items[currentIndex + 1]?.batchType;
            } else if (items.length > 1) {
              nextItemId = items[currentIndex - 1]?.id;
              nextBatchType = items[currentIndex - 1]?.batchType;
            }
          }
          setTimeout(() => {
            setActiveItem(nextItemId);
            setBatchType(nextBatchType);
          }, 500);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTimeout(() => {
          callback();
        }, 500);
      }
    }
  };

  const handleApprove = useCallback(async () => {
    const payload = {
      batchId: batch?.id,
    };

    const isHybrid = batch?.batchType === 'HYBRID';

    setIsApproving(true);
    try {
      let response;
      if (isHybrid) {
        response = await approveBatch(payload);
      } else {
        payload.approvalType = 'approve';
        response = await handleApproval(payload);
      }

      // SUCCESS  7017 for hybrid
      if ((isHybrid && response.statusCode === 7017) || response?.statusCode === 7009 || response?.statusCode === 0 || response?.statusCode === 4004) {
        message.success(
          t(JOB_APPROVAL_MESSAGES.APPROVE_SUCCESS.key, JOB_APPROVAL_MESSAGES.APPROVE_SUCCESS.value),
        );
        setShowApprove(false);
        return;
        // onApproveJob?.(batch.id);
      }

      // FAILURE WITH ERROR CODE
      const errorCode = response?.errors?.[0]?.code;

      let errorMessageConfig;

      switch (errorCode) {
        case 7005:
          errorMessageConfig = JOB_APPROVAL_MESSAGES.BATCH_NOT_FOUND;
          break;
        case 7008:
          errorMessageConfig = JOB_APPROVAL_MESSAGES.INVALID_STATE;
          break;
        case 7009:
          errorMessageConfig = JOB_APPROVAL_MESSAGES.NOT_AUTHORIZED;
          break;
        default:
          errorMessageConfig = JOB_APPROVAL_MESSAGES.APPROVE_FAILED;
      }

      message.error(t(errorMessageConfig.key, errorMessageConfig.value));
    } catch (error) {
      console.error('Error approving batch:', error);

      message.error(
        t(JOB_APPROVAL_MESSAGES.APPROVE_ERROR.key, JOB_APPROVAL_MESSAGES.APPROVE_ERROR.value),
      );
    } finally {
      setIsApproving(false);
      console.log('Approval process function over for batch:', batch?.id);
    }
  }, [batch?.id, t]);

  const handleAbort = useCallback(async (data) => {
    const { reason, email = [], isNotified } = data;
    const payload = {
      batchId: batch?.id,
      rejectReason: reason,
      email: isNotified ? email : []
    };

    const isHybrid = batch?.batchType === 'HYBRID';

    setIsAborting(true);
    try {
      let response;
      if (isHybrid) {
        response = await abortBatch(payload);
      } else {
        payload.approvalType = 'reject';
        response = await abortAdvancedBatch(payload);
      }

      // SUCCESS
      if (response?.statusCode === 7011) {
        message.success(
          t(JOB_ABORT_MESSAGES.ABORT_SUCCESS.key, JOB_ABORT_MESSAGES.ABORT_SUCCESS.value),
        );
        setShowApprovalCard(false);
        return;
      }

      // FAILURE WITH ERROR CODE
      const errorCode = response?.errors?.[0]?.code;

      let errorMessageConfig;

      switch (errorCode) {
        case 7005:
          errorMessageConfig = JOB_ABORT_MESSAGES.BATCH_NOT_FOUND;
          break;
        case 7011:
          errorMessageConfig = JOB_ABORT_MESSAGES.ALREADY_STARTED_OR_COMPLETED;
          break;
        default:
          errorMessageConfig = JOB_ABORT_MESSAGES.ABORT_FAILED;
      }
      message.error(t(errorMessageConfig.key, errorMessageConfig.value));
    } catch (error) {
      console.error('Error aborting batch:', error);
      message.error(t(JOB_ABORT_MESSAGES.ABORT_ERROR.key, JOB_ABORT_MESSAGES.ABORT_ERROR.value));
    } finally {
      setIsAborting(false);
      console.log('Abort process function over for batch:', batch?.id);
    }
  }, [batch?.id, t]);

  const handleReject = useCallback(async (data) => {
    const { reason, email = [], isNotified } = data;
    const payload = {
      batchId: batch?.id,
      rejectReason: reason,
      email: isNotified ? email : []
    };

    const isHybrid = batch?.batchType === 'HYBRID';

    setIsRejecting(true);
    try {
      let response;
      if (isHybrid) {
        response = await rejectHybridBatch(payload);
      } else {
        payload.approvalType = 'reject';
        response = await handleApproval(payload);
      }

      // SUCCESS
      if (isHybrid ? response.statusCode === 7036 : response?.statusCode === 7011 || response?.statusCode === 4004) {
        message.success(
          t(JOB_REJECT_MESSAGES.REJECT_SUCCESS.key, JOB_REJECT_MESSAGES.REJECT_SUCCESS.value),
        );
        setShowApprovalCard(false);
        return;
      }

      // REJECTED
      if (response?.statusCode === 4004) {
        message.success(t(JOB_DETAILS_UI.REJECT_SUCCESS.key, JOB_DETAILS_UI.REJECT_SUCCESS.value));
      }

      // FAILURE WITH ERROR CODE
      const errorCode = response?.errors?.[0]?.code;

      let errorMessageConfig;

      switch (errorCode) {
        case 7005:
          errorMessageConfig = JOB_ABORT_MESSAGES.BATCH_NOT_FOUND;
          break;
        case 7008:
        case 7011:
          errorMessageConfig = JOB_ABORT_MESSAGES.ALREADY_STARTED_OR_COMPLETED;
          break;
        case 7009:
          errorMessageConfig = JOB_REJECT_MESSAGES.NOT_AUTHORIZED;
        default:
          errorMessageConfig = JOB_REJECT_MESSAGES.REJECT_FAILED;
      }
      message.error(t(errorMessageConfig.key, errorMessageConfig.value));
    } catch (error) {
      console.error('Error rejecting batch:', error);

      message.error(t(JOB_REJECT_MESSAGES.REJECT_ERROR.key, JOB_REJECT_MESSAGES.REJECT_ERROR.value));
    } finally {
      setIsRejecting(false);
      console.log('Reject process function over for batch:', batch?.id);
    }
  }, [batch?.id, t]);

  const handleReschedule = useCallback(async () => {
    if (!newDate || !newTime) {
      message.error(
        t(
          JOB_RESCHEDULE_MESSAGES.MISSING_DATE_TIME.key,
          JOB_RESCHEDULE_MESSAGES.MISSING_DATE_TIME.value,
        ),
      );
    }

    try {
      const utcTime = moment(newTime, 'HH:mm:ss')
        .utc()
        .format('HH:mm:ss');
      let newScheduledTime = `${newDate} ${utcTime}`;

      const payload = {
        batchId: batch?.id,
        newScheduledTime,
      };

      const isHybrid = batch?.batchType === 'HYBRID';
      let response;

      setIsRescheduling(true);
      if (isHybrid) {
        response = await rescheduleBatch(payload);
      } else {
        response = await rescheduleAdvancedBatch(payload);
      }

      // SUCCESS
      if ((isHybrid && response.statusCode === 7006) || response?.data?.statusCode === 7006 || response?.data?.statusCode === 7004) {
        message.success(
          t(
            JOB_RESCHEDULE_MESSAGES.RESCHEDULE_SUCCESS.key,
            JOB_RESCHEDULE_MESSAGES.RESCHEDULE_SUCCESS.value,
          ),
        );
        setShowReschedule(false);
        return;
      }

      // FAILURE WITH ERROR CODE
      const errorCode = response?.errors?.[0]?.code;

      let errorMessageConfig;

      switch (errorCode) {
        case 7005:
          errorMessageConfig = JOB_RESCHEDULE_MESSAGES.BATCH_NOT_FOUND;
          break;
        default:
          errorMessageConfig = JOB_RESCHEDULE_MESSAGES.RESCHEDULE_FAILED;
      }

      message.error(t(errorMessageConfig.key, errorMessageConfig.value));
    } catch (error) {
      console.error('Error rescheduling batch:', error);

      message.error(
        t(
          JOB_RESCHEDULE_MESSAGES.RESCHEDULE_ERROR.key,
          JOB_RESCHEDULE_MESSAGES.RESCHEDULE_ERROR.value,
        ),
      );
    } finally {
      setIsRescheduling(false);
      console.log('Reschedule process function over for batch:', batch?.id);
    }
  }, [batch?.id, t, newDate, newTime]);

  const handleDateChange = (date, dateString) => {
    setNewDate(dateString);
  };

  const handleTimeChange = (time, timeString) => {
    setNewTime(timeString);
  };

  const canUserApprove = () => {
    if (batch?.approvalType === 'LEVEL') {
      return getJobStatus(batch?.status, userDetails?.roles) && userDetails.roles.includes('JOBAPPROVERL'+batch?.status?.at(-1));
    } else if (batch?.approvalType === 'USER') {
      return parseInt(batch?.approver) === userDetails?.id;
    } else if(batch?.approvalType === null && batch?.scheduledTime?.length > 0 && (batch?.triggeredBy === userDetails.id || batch.batchType === 'HYBRID')) {
      return !isBatchTimePassed;
    }
  };

  if (batch === undefined) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100%',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img src={'tech_loader.gif'} style={{ width: '100px' }} alt="" />
      </div>
    );
  }

  // Common container for tabs
  const renderTabs = (isSmallStatus = true, isAdvancedBatch = false, isDraft = false) =>
    isDraft ? (
      <S.TabsWrapper>
        <S.TabContent>
          <JobDetailsTab batch={batch} isDraft={true} users={users} />
        </S.TabContent>
      </S.TabsWrapper>
    ) : (
      <S.TabsWrapper>
        <S.TabsHeader>
          <S.TabButton active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
            {t(JOB_DETAILS_UI.JOB_DETAILS_TAB.key, JOB_DETAILS_UI.JOB_DETAILS_TAB.value)}
            {activeTab === 'details' && <S.TabIndicator />}
          </S.TabButton>
          {isAdvancedBatch && (
            <S.TabButton active={activeTab === 'proofs'} onClick={() => setActiveTab('proofs')}>
              {t(JOB_DETAILS_UI.PROOFS_PREVIEW_TAB.key, JOB_DETAILS_UI.PROOFS_PREVIEW_TAB.value)}
              {activeTab === 'proofs' && <S.TabIndicator />}
            </S.TabButton>
          )}
          <S.TabButton active={activeTab === 'status'} onClick={() => setActiveTab('status')}>
            {t(JOB_DETAILS_UI.JOB_STATUS_TAB.key, JOB_DETAILS_UI.JOB_STATUS_TAB.value)}
            {activeTab === 'status' && <S.TabIndicator />}
          </S.TabButton>
        </S.TabsHeader>

        <S.TabContent>
          {activeTab === 'details' && <JobDetailsTab batch={batch} users={users} />}
          {activeTab === 'proofs' && <ProofsTab batch={batch} />}
          {activeTab === 'status' && (
            <StatusTab
              jobId={jobId}
              users={users}
              subscribed={subscribed}
              filters={{...filters, filter:{...filters.filter, batchType}}}
              large={!isSmallStatus}
            />
          )}
        </S.TabContent>
      </S.TabsWrapper>
    );

  const isHybridDraft = hybridDraftStatus.includes(batch?.status);
  // Draft job view
  if (batch?.status === 'draft' || isHybridDraft) {
    return (
      <S.PanelContainer>
        <BatchDetailsHeader batch={batch} handleCancel={handleCancel} showSidebar={showSidebar} />
        <DraftStatus
          batch={batch}
          onContinueEdit={handleContinueEditing}
          isHybridDraft={isHybridDraft}
          handleCancel={handleCancel}
        />
        {renderTabs(true, batch?.batchType === 'ADVANCED', isHybridDraft ? false : true)}
      </S.PanelContainer>
    );
  }
  return (
    <S.PanelContainer>
      <BatchDetailsHeader batch={batch} handleCancel={handleCancel} showSidebar={showSidebar} />
      {batch.status === 'ABORTED' && <AbortionDetailsTab batch={batch} users={users} />}
      {showApprovalCard && (
        <ApprovalStatus
          batch={batch}
          showReschedule={showReschedule}
          setShowReschedule={setShowReschedule}
          newDate={newDate}
          newTime={newTime}
          handleDateChange={handleDateChange}
          handleTimeChange={handleTimeChange}
          handleReschedule={handleReschedule}
          handleApprove={handleApprove}
          handleAbort={handleAbort}
          handleReject={handleReject}
          isApproving={isApproving}
          isAborting={isAborting}
          isRejecting={isRejecting}
          isRescheduling={isRescheduling}
          users={users}
          showApprove={showApprove}
          canAbortBatch={canAbortBatch}
          isBatchTimePassed={isBatchTimePassed}
        />
      )}
      {renderTabs(true, batch?.batchType === 'ADVANCED')}
    </S.PanelContainer>
  );
};

export default BatchDetailsPanel;
